import React, { useState, useCallback, useEffect } from 'react';
import SEO from '../../components/SEO/SEO';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useParams } from 'react-router-dom';

import style from '../ProfilePage/ProfilePage.module.css'
import StrydCardSmall from '../../components/StrydCardSmall/StrydCardSmall';
import Tag from '../../components/Tag/Tag'
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import RanksModal from '../../components/RanksModal/RanksModal';
import SocialConnectionsModal from '../../components/SocialConnectionsModal/SocialConnectionsModal';
import AppLoader from '../../components/AppLoader/AppLoader';
import { encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import formatDisplayName from '../../utils/formatDisplayName';
import { getMealEntriesForDay } from '../../utils/mealPlan';
import { MdExpandMore, MdExpandLess, MdWorkspacePremium } from 'react-icons/md';
import { normalizeRoutineCreationType, normalizeUserPayload } from '../../utils/userData';

import rankData from '../../utils/ranks';
import findUserRank from '../../utils/findUserRank';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';
import { checkSubscription } from '../../utils/checkSubscription';

import { useAuthUser } from '../../hooks/useAuthUser';

const UserProfile = () => {
	const { userID } = useParams();
	const { user, isLoaded } = useAuthUser()
	const navigate = useNavigate()
	const API_URL = process.env.REACT_APP_API_URL;

	const [person, setPerson] = useState(null);
	const [isProfileLoading, setIsProfileLoading] = useState(true);
	const [profileError, setProfileError] = useState('');
	const [viewerProfile, setViewerProfile] = useState(null);
	const [following, setFollowing] = useState(false)
	const [followerNumber, setFollowerNumber] = useState(0)
	const [isRanksModalOpen, setIsRanksModalOpen] = useState(false)
	const [activeTab, setActiveTab] = useState('routines');
	const [profilePosts, setProfilePosts] = useState([]);
	const [loadingProfilePosts, setLoadingProfilePosts] = useState(false);
	const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
	const [socialModalType, setSocialModalType] = useState('followers');
	const [updatingFollowUserID, setUpdatingFollowUserID] = useState('');
	const [isBlockedByUser, setIsBlockedByUser] = useState(false);
	const [blockedMessage, setBlockedMessage] = useState('');
	const [hasBlockedUser, setHasBlockedUser] = useState(false);
	const [isBlockActionLoading, setIsBlockActionLoading] = useState(false);
	const [showBlockConfirm, setShowBlockConfirm] = useState(false);
	const [showUpgradeModal, setShowUpgradeModal] = useState(false);
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), [API_URL]);
	const getRoutinePath = useCallback(
		(routine) => `/stryd/${encodePathSegment(userID)}/${encodePathSegment(routine)}`,
		[userID]
	);


	const getUser = useCallback(async () => {
		setIsProfileLoading(true);
		setProfileError('');
		setIsBlockedByUser(false);
		setBlockedMessage('');
		try {
			const viewerParam = user?.id ? `?viewerID=${encodePathSegment(user.id)}` : '';
			const res = await requestApi((base) => axios.get(`${base}/users/${encodePathSegment(userID)}${viewerParam}`));
			const profile = normalizeUserPayload(res.data);
			setPerson(profile);
			if (!profile) {
				setProfileError('This profile does not exist or is unavailable.');
			}
		} catch (err) {
			if (err?.response?.status === 403 && err?.response?.data?.error === 'blocked') {
				setIsBlockedByUser(true);
				setBlockedMessage(err.response.data.message || 'You have been blocked by this user.');
				setPerson(null);
			} else {
				console.log("Error:", err.message);
				setPerson(null);
				setProfileError('We could not load this profile right now.');
			}
		} finally {
			setIsProfileLoading(false);
		}
	}, [requestApi, userID, user?.id]);

	const getViewer = useCallback(() => {
		if (!user?.id) return;
		requestApi((base) => axios.get(`${base}/users/${encodePathSegment(user.id)}`))
			.then((res) => setViewerProfile(normalizeUserPayload(res.data)))
			.catch((err) => console.log('Viewer fetch error:', err.message));
	}, [requestApi, user?.id]);

	const getProfilePosts = useCallback(async () => {
		setLoadingProfilePosts(true);
		try {
			const response = await requestApi((base) => axios.get(`${base}/feed/user/${userID}`));
			setProfilePosts(response.data.posts || []);
		} catch (error) {
			console.error('Profile posts error:', error.message);
			setProfilePosts([]);
		} finally {
			setLoadingProfilePosts(false);
		}
	}, [requestApi, userID]);

	const handleOpenRanksModal = () => {
		setIsRanksModalOpen(true)
	}

	const handleCloseRanksModal = () => {
		setIsRanksModalOpen(false)
	}


	const openSocialModal = (type) => {
		setSocialModalType(type);
		setIsSocialModalOpen(true);
	};

	const socialEntries = socialModalType === 'followers'
		? (person?.socials?.followers || [])
		: (person?.socials?.followings || []);
	const currentFollowingIDs = new Set((viewerProfile?.socials?.followings || []).map((entry) => entry.userID));

	const toggleFollowFromModal = async (targetUser) => {
		if (!user?.id || !targetUser?.userID || targetUser.userID === user.id) return;
		const isFollowingTarget = currentFollowingIDs.has(targetUser.userID);
		setUpdatingFollowUserID(targetUser.userID);
		try {
			await requestApi((base) => axios.post(`${base}/users/${targetUser.userID}/${isFollowingTarget ? 'unfollow' : 'follow'}`, {
				userID: user.id,
				username: user.username || viewerProfile?.username || user.firstName || 'User',
				profileImageLink: user.profileImageUrl
			}));

			setViewerProfile((prev) => {
				if (!prev) return prev;
				const nextFollowings = isFollowingTarget
					? (prev.socials?.followings || []).filter((entry) => entry.userID !== targetUser.userID)
					: [...(prev.socials?.followings || []), {
						userID: targetUser.userID,
						username: targetUser.username,
						profileImageLink: targetUser.profileImageLink
					}];
				return {
					...prev,
					socials: {
						...(prev.socials || {}),
						followings: nextFollowings
					}
				};
			});

			if (targetUser.userID === person?.userID) {
				setFollowing(!isFollowingTarget);
				setFollowerNumber((prev) => isFollowingTarget ? Math.max(prev - 1, 0) : prev + 1);
			}
		} catch (error) {
			console.error('Follow toggle error:', error.message);
		} finally {
			setUpdatingFollowUserID('');
		}
	};


	const followUser = async (targetUserID, followerData) => {
		try {
			await requestApi((base) => axios.post(`${base}/users/${targetUserID}/follow`, followerData));
			setFollowing(true);
			setFollowerNumber((prev) => prev + 1);
			setViewerProfile((prev) => {
				if (!prev || !person) return prev;
				const followings = prev.socials?.followings || [];
				if (followings.some((entry) => entry.userID === person.userID)) return prev;
				return {
					...prev,
					socials: {
						...(prev.socials || {}),
						followings: [...followings, {
							userID: person.userID,
							username: person.username,
							profileImageLink: person.profileImageLink
						}]
					}
				};
			});
			setPerson((prev) => {
				if (!prev || !user) return prev;
				const followers = prev.socials?.followers || [];
				if (followers.some((entry) => entry.userID === user.id)) return prev;
				return {
					...prev,
					socials: {
						...(prev.socials || {}),
						followers: [...followers, {
							userID: user.id,
							username: user.username || viewerProfile?.username || user.firstName || 'User',
							profileImageLink: user.profileImageUrl
						}]
					}
				};
			});
			toast('Followed user');
		} catch (error) {
			console.error(error.response?.data || error.message);
			toast('Error occurred');
		}
	};

	const unfollowUser = async (targetUserID, unfollowerData) => {
		try {
			await requestApi((base) => axios.post(`${base}/users/${targetUserID}/unfollow`, unfollowerData));
			setFollowing(false);
			setFollowerNumber((prev) => Math.max(prev - 1, 0));
			setViewerProfile((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					socials: {
						...(prev.socials || {}),
						followings: (prev.socials?.followings || []).filter((entry) => entry.userID !== targetUserID)
					}
				};
			});
			setPerson((prev) => {
				if (!prev || !user) return prev;
				return {
					...prev,
					socials: {
						...(prev.socials || {}),
						followers: (prev.socials?.followers || []).filter((entry) => entry.userID !== user.id)
					}
				};
			});
			toast('Unfollowed user');
		} catch (error) {
			console.error(error.response?.data || error.message);
		}
	};


	const checkBlockStatus = useCallback(async () => {
		if (!user?.id || !userID || user.id === userID) return;
		try {
			const res = await requestApi((base) => axios.get(
				`${base}/users/${encodePathSegment(userID)}/block-status/${encodePathSegment(user.id)}`
			));
			setHasBlockedUser(res.data?.hasBlockedOwner || false);
		} catch {
			// non-critical
		}
	}, [requestApi, userID, user?.id]);

	const handleBlockUser = async () => {
		if (!user?.id || !userID || isBlockActionLoading) return;
		setIsBlockActionLoading(true);
		try {
			await requestApi((base) => axios.post(`${base}/users/${encodePathSegment(user.id)}/block`, {
				targetUserID: userID
			}));
			setHasBlockedUser(true);
			setShowBlockConfirm(false);
			setFollowing(false);
			toast('User blocked');
		} catch (err) {
			console.error('Block error:', err.message);
			toast('Could not block user');
		} finally {
			setIsBlockActionLoading(false);
		}
	};

	const handleUnblockUser = async () => {
		if (!user?.id || !userID || isBlockActionLoading) return;
		setIsBlockActionLoading(true);
		try {
			await requestApi((base) => axios.post(`${base}/users/${encodePathSegment(user.id)}/unblock`, {
				targetUserID: userID
			}));
			setHasBlockedUser(false);
			toast('User unblocked');
		} catch (err) {
			console.error('Unblock error:', err.message);
			toast('Could not unblock user');
		} finally {
			setIsBlockActionLoading(false);
		}
	};

	useEffect(() => {
		if (!isLoaded) return;
		getUser();
	}, [getUser, isLoaded]);

	useEffect(() => {
		if (user && person) {
			if (user.id === userID) {
				navigate('/profile-page');
			}

			const followersArr = person?.socials?.followers || [];
			const isFollowingUser = followersArr.some((entry) => entry.userID === user.id);
			setFollowing(isFollowingUser);
			setFollowerNumber(followersArr.length);
		}
	}, [person, user, userID, navigate]);

	useEffect(() => {
		if (!isLoaded) return;
		getViewer();
		checkBlockStatus();
	}, [getViewer, checkBlockStatus, isLoaded]);

	useEffect(() => {
		if (!isLoaded || activeTab !== 'posts') return;
		getProfilePosts();
	}, [getProfilePosts, isLoaded, activeTab]);

	const workoutInterests = person?.interests?.workoutInterests || [];
	const personGeneratedRoutines = (person?.stryds?.routines || []).filter(
		(routine) => normalizeRoutineCreationType(routine?.routineCreationType) === 'generated'
	);
	const personCreatedRoutines = (person?.stryds?.routines || []).filter(
		(routine) => normalizeRoutineCreationType(routine?.routineCreationType) === 'created'
	);
	const personAddedRoutines = person?.createdStryds?.routines || [];

	if (!isLoaded || isProfileLoading) {
		return <AppLoader message='Loading profile...' />;
	}

	if (isBlockedByUser) {
		return (
			<div className={style.ProfilePage}>
				<div className={style.EmptyStateCard}>
					<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem 1rem', textAlign: 'center' }}>
						<div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--bg-secondary, #f3f4f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
							🚫
						</div>
						<span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary, #111)' }}>
							{blockedMessage}
						</span>
						<span style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #666)', lineHeight: 1.5 }}>
							You cannot view this profile, their routines, or interact with them.
						</span>
						<PrimaryButton text='Go back' click={() => navigate(-1)} width='160px' />
					</div>
				</div>
			</div>
		);
	}

	if (profileError) {
		return (
			<div className={style.ProfilePage}>
				<div className={style.EmptyStateCard}>
					<span className={style.EmptyText}>{profileError}</span>
					<PrimaryButton text='Go back' click={() => navigate(-1)} width='140px' />
				</div>
			</div>
		);
	}



	return (
		<div className={style.ProfilePage}>
			<SEO
				title={person?.username ? `${formatDisplayName(person.username)} — Profile` : 'Profile'}
				description={person?.username ? `${formatDisplayName(person.username)}'s fitness profile on Calcium. Total XP: ${person.totalXP || 0}. Follow to stay motivated.` : 'View a Calcium user profile.'}
				path={`/profile/${userID}`}
				image={person?.profileImageLink}
			/>
			{person ? <>

				{isRanksModalOpen && (
					<RanksModal
						onClose={handleCloseRanksModal}
						ranks={rankData}
						currentXP={person?.totalXP || 0}
					/>
				)}
				<SocialConnectionsModal
					isOpen={isSocialModalOpen}
					title={socialModalType === 'followers' ? 'Followers' : 'Following'}
					users={socialEntries}
					onClose={() => setIsSocialModalOpen(false)}
					currentUserID={user?.id}
					currentFollowingIDs={currentFollowingIDs}
					onToggleFollow={toggleFollowFromModal}
					isUpdatingUserID={updatingFollowUserID}
				/>


				<div className={style.ProfileInfoWrapper}>
					<div className={style.ProfileUserStats}>
						<div>
							<div className={style.ProfileInfo}>
								<img
									className={style.ProfilePicture}
									src={person.profileImageLink || user?.profileImageUrl || ''}
									alt={`${formatDisplayName(person.username || 'User')} profile`} />

								<div className={style.ProfileInfoDetails}>
									<div className={style.ProfileInfoText}>
										<span className={style.ProfileInfoUsername}>
											{formatDisplayName(person.username)}
											<VerifiedBadge userPlan={person.userPlan} totalXP={person.totalXP} size='large' />
											{!checkSubscription(person) && <span className={style.UsernameRankBadge}>{findUserRank(person.totalXP)?.emoji}</span>}
										</span>

										<button
											type="button"
											className={style.ProfileRank}
											onClick={handleOpenRanksModal}
										>
											{findUserRank(person.totalXP)?.rank}
										</button>
									</div>

									<div className={style.ProfileAttributesWrapper}>
										<button
											type='button'
											className={`${style.ProfileAttribute} ${style.ProfileStatButton}`}
											onClick={() => openSocialModal('followers')}
										>
											<span className={style.ProfileAttributeHeading}>
												Followers
											</span>

											<span className={style.ProfileCount}>
												{followerNumber}
											</span>
										</button>

										<button
											type='button'
											className={`${style.ProfileAttribute} ${style.ProfileStatButton}`}
											onClick={() => openSocialModal('following')}
										>
											<span className={style.ProfileAttributeHeading}>
												Following
											</span>

											<span className={style.ProfileCount}>
												{(person?.socials?.followings || []).length}
											</span>
										</button>

										<div className={style.ProfileAttribute}>
											<span className={style.ProfileAttributeHeading}>
												Total XP
											</span>

											<span className={style.ProfileCount}>
												{person.totalXP || 0}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>


						{user && (
							<div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
								{following ? (
									<PrimaryButton
										text="Following"
										click={() => unfollowUser(
											userID, {
											username: user.username,
											profileImageLink: user.profileImageUrl,
											userID: user.id
										}
										)}
									/>
								) : (
									<PrimaryButton
										text="Follow"
										click={() => followUser(
											userID,
											{
												username: user.username,
												profileImageLink: user.profileImageUrl,
												userID: user.id
											}
										)}
									/>
								)}
								<SecondaryButton
									text="Message"
									click={async () => {
										if (!checkSubscription(viewerProfile)) {
											setShowUpgradeModal(true);
											return;
										}
										try {
											await requestApi((base) => axios.post(`${base}/chat/conversation`, {
												participants: [
													{ userID: user.id, username: user.username, profileImageLink: user.profileImageUrl },
													{ userID: person.userID, username: person.username, profileImageLink: person.profileImageLink }
												]
											}));
											navigate('/chat');
										} catch (err) {
											console.error('Chat error:', err.message);
											toast('Could not start conversation');
										}
									}}
								/>
								<button
									type="button"
									onClick={() => hasBlockedUser ? handleUnblockUser() : setShowBlockConfirm(true)}
									disabled={isBlockActionLoading}
									style={{
										padding: '0.5rem 1rem',
										borderRadius: 10,
										border: '1px solid var(--border-color, #e5e7eb)',
										background: hasBlockedUser ? 'var(--bg-secondary, #f3f4f6)' : 'transparent',
										color: hasBlockedUser ? 'var(--text-primary, #111)' : '#dc2626',
										fontSize: '0.8rem',
										fontWeight: 600,
										cursor: isBlockActionLoading ? 'wait' : 'pointer',
										opacity: isBlockActionLoading ? 0.6 : 1,
										transition: 'all 0.15s ease',
										whiteSpace: 'nowrap',
									}}
								>
									{isBlockActionLoading ? '...' : hasBlockedUser ? 'Unblock' : 'Block'}
								</button>
							</div>
						)}

						{showBlockConfirm && (
							<div style={{
								position: 'fixed', inset: 0, zIndex: 1000,
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								background: 'rgba(0,0,0,0.45)', padding: '1rem',
							}}>
								<div style={{
									background: 'var(--card-bg, #fff)', borderRadius: 16, padding: '1.75rem',
									maxWidth: 380, width: '100%', textAlign: 'center',
									display: 'flex', flexDirection: 'column', gap: '1rem',
									boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
								}}>
									<span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary, #111)' }}>
										Block {formatDisplayName(person?.username || 'this user')}?
									</span>
									<span style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #666)', lineHeight: 1.5 }}>
										They won't be able to see your profile, routines, or interact with you. You'll also be unfollowed from each other.
									</span>
									<div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '0.25rem' }}>
										<button
											onClick={() => setShowBlockConfirm(false)}
											style={{
												flex: 1, padding: '0.625rem', borderRadius: 10, border: 'none',
												background: 'var(--bg-secondary, #f3f4f6)', color: 'var(--text-primary, #111)',
												fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
											}}
										>
											Cancel
										</button>
										<button
											onClick={handleBlockUser}
											disabled={isBlockActionLoading}
											style={{
												flex: 1, padding: '0.625rem', borderRadius: 10, border: 'none',
												background: '#dc2626', color: '#fff',
												fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
												opacity: isBlockActionLoading ? 0.6 : 1,
											}}
										>
											{isBlockActionLoading ? 'Blocking...' : 'Block'}
										</button>
									</div>
								</div>
							</div>
						)}

						{showUpgradeModal && (
							<div style={{
								position: 'fixed', inset: 0, zIndex: 1000,
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								background: 'rgba(0,0,0,0.5)', padding: '1rem',
							}}>
								<div style={{
									background: 'var(--card-bg, #fff)', borderRadius: 20, padding: '2rem 1.75rem',
									maxWidth: 400, width: '100%', textAlign: 'center',
									display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
									boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
								}}>
									<div style={{
										width: 64, height: 64, borderRadius: '50%',
										background: 'linear-gradient(135deg, rgba(108,92,231,0.15), rgba(34,197,94,0.1))',
										display: 'flex', alignItems: 'center', justifyContent: 'center',
									}}>
										<MdWorkspacePremium style={{ fontSize: '1.8rem', color: 'var(--accent-primary, #6c5ce7)' }} />
									</div>
									<span style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary, #111)' }}>
										Messaging is a Plus Feature
									</span>
									<span style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #666)', lineHeight: 1.6 }}>
										Upgrade to Calcium Plus to send direct messages, connect with athletes, and build your fitness network.
									</span>
									<Link
										to='/upgrade'
										style={{
											display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
											padding: '0.625rem 2rem', borderRadius: 12,
											background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)', color: '#fff',
											fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none', marginTop: '0.25rem',
											boxShadow: '0 8px 24px rgba(108,92,231,0.3)',
										}}
									>
										Upgrade to Calcium Plus
									</Link>
									<button
										type='button'
										onClick={() => setShowUpgradeModal(false)}
										style={{
											background: 'none', border: 'none', color: 'var(--text-muted, #999)',
											fontSize: '0.8rem', cursor: 'pointer', padding: '0.5rem',
										}}
									>
										Maybe later
									</button>
								</div>
							</div>
						)}

						<div>
							<div>
								<span>
									<div className={style.UserInterests}>
										{workoutInterests.length > 0 ? (
											workoutInterests.map((interest) => (
												<Tag label={interest} key={interest} />
											))
										) : (
											<span className={style.EmptyText}>
												No workout interests
											</span>
										)}
									</div>

								</span>
							</div>
						</div>
					</div>
				</div>

				<div className={style.UserRoutines}>
					<div className={style.TabsContainer}>
						<button
							className={`${style.Tab} ${activeTab === 'routines' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('routines')}
						>
							Routines
						</button>
						<button
							className={`${style.Tab} ${activeTab === 'meals' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('meals')}
						>
							Meals
						</button>
						<button
							className={`${style.Tab} ${activeTab === 'posts' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('posts')}
						>
							Posts
						</button>
					</div>

					{activeTab === 'routines' ? (
					<>
						<div className={style.CardsWrapper}>
							<span className='SectionTitle'>Generated Routines</span>
							{personGeneratedRoutines.length > 0 ? (
								<div className={style.CardsContainer}>
									{personGeneratedRoutines.map((routine, index) => (
										<Link key={routine._id || `gen-${index}`} to={getRoutinePath(routine.routineName)}>
											<StrydCardSmall
												name={routine.routineName}
												time={routine.estimatedTimeMinutes}
												creator={person.username}
											/>
										</Link>
									))}
								</div>
							) : (
								<span className={style.EmptyText}>No generated routines yet.</span>
							)}
						</div>

						<div className={style.CardsWrapper}>
							<span className='SectionTitle'>Created Routines</span>
							{personCreatedRoutines.length > 0 ? (
								<div className={style.CardsContainer}>
									{personCreatedRoutines.map((routine, index) => (
										<Link key={routine._id || `created-${index}`} to={getRoutinePath(routine.routineName)}>
											<StrydCardSmall
												name={routine.routineName}
												time={routine.estimatedTimeMinutes || routine.duration}
												creator={person.username}
											/>
										</Link>
									))}
								</div>
							) : (
								<span className={style.EmptyText}>No created routines yet.</span>
							)}
						</div>

						{personAddedRoutines.length > 0 && (
							<div className={style.CardsWrapper}>
								<span className='SectionTitle'>Added Routines</span>
								<div className={style.CardsContainer}>
									{personAddedRoutines.map((routine, index) => (
										<Link key={routine._id || `added-${index}`} to={getRoutinePath(routine.routineName)}>
											<StrydCardSmall
												name={routine.routineName}
												time={routine.duration}
											/>
										</Link>
									))}
								</div>
							</div>
						)}
					</>
					) : activeTab === 'meals' ? (
						<PublicMealsSection meals={person?.meals} />
					) : (
						<div className={style.MealsTabContent}>
							<span className='SectionTitle'>Posts</span>
							{loadingProfilePosts ? (
								<p className={style.EmptyText}>Loading posts...</p>
							) : profilePosts.length > 0 ? (
								<div className={style.PostsList}>
									{profilePosts.map((post) => (
										<Link key={post._id} to={`/feed/post/${post._id}`} className={style.PostPreviewLink}>
											<div className={style.PostPreviewCard}>
												<p className={style.PostPreviewText}>{post.content || 'Shared an update'}</p>
												<div className={style.PostPreviewMeta}>
													<span>{new Date(post.createdAt).toLocaleDateString()}</span>
													<span>{post.likes?.length || 0} {(post.likes?.length || 0) === 1 ? 'like' : 'likes'}</span>
													<span>{post.comments?.length || 0} {(post.comments?.length || 0) === 1 ? 'reply' : 'replies'}</span>
												</div>
											</div>
										</Link>
									))}
								</div>
							) : (
								<p className={style.EmptyText}>No posts yet.</p>
							)}
						</div>
					)}
				</div>

			</> : null}
		</div >
	)
}

const MEAL_EMOJI = {
	breakfast: '🌅',
	lunch: '☀️',
	dinner: '🌙',
	snack: '🍎',
	snacks: '🍎'
};

function PublicMealsSection({ meals }) {
	const [expandedPlan, setExpandedPlan] = useState(null);
	const [expandedDay, setExpandedDay] = useState(null);

	if (!meals || meals.length === 0) {
		return (
			<div className={style.MealsTabContent}>
				<span className='SectionTitle'>Meals</span>
				<p className={style.EmptyText}>No meal plans yet.</p>
			</div>
		);
	}

	return (
		<div className={style.MealsTabContent}>
			<span className='SectionTitle'>Meals</span>
			<div className={style.MealPlansList}>
				{meals.map((mealPlan, idx) => {
					const isOpen = expandedPlan === idx;
					return (
						<div key={idx} className={style.MealPlanCard}>
							<button
								type="button"
								className={style.MealPlanHeader}
								onClick={() => {
									setExpandedPlan(isOpen ? null : idx);
									setExpandedDay(null);
								}}
							>
								<div className={style.MealPlanHeaderText}>
									<span className={style.MealPlanTitle}>{mealPlan.planName}</span>
									<span className={style.MealPlanDate}>{mealPlan.createdDate}</span>
								</div>
								<span className={style.MealPlanToggle}>
									{isOpen ? <MdExpandLess /> : <MdExpandMore />}
								</span>
							</button>

							{isOpen && mealPlan.days && (
								<div className={style.MealDaysGrid}>
									{mealPlan.days.map((day, dayIdx) => {
										const mealEntries = getMealEntriesForDay(day);
										const isDayOpen = expandedDay === dayIdx;
										return (
											<div key={dayIdx} className={style.MealDayCard}>
												<button
													type="button"
													className={style.MealDayHeader}
													onClick={() => setExpandedDay(isDayOpen ? null : dayIdx)}
												>
													<span className={style.MealDayLabel}>Day {dayIdx + 1}</span>
													<span className={style.MealDayCount}>
														{mealEntries.length} meals
													</span>
												</button>

												{isDayOpen && (
													<div className={style.MealItemsGrid}>
														{mealEntries.map(({ mealType, name, calories }) => (
															<div key={mealType} className={style.MealItemCard}>
																<span className={style.MealItemEmoji}>
																	{MEAL_EMOJI[mealType.toLowerCase()] || '🍽️'}
																</span>
																<div className={style.MealItemInfo}>
																	<span className={style.MealType}>{mealType}</span>
																	<span className={style.MealName}>{name}</span>
																	{calories !== null && (
																		<span className={style.MealDayCount}>{calories} cal</span>
																	)}
																</div>
															</div>
														))}
													</div>
												)}
											</div>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default UserProfile;